import React, { useEffect, useState, useMemo, useCallback } from "react";
import styled from "styled-components";
import { Modal } from "semantic-ui-react";
import { MenuSettingService } from "utility/services/menu-setting";
import { useNotification } from "hooks/toast";
import { LoaderBar, FlexRow } from "../../components";
import { AppButton, AppPrimaryButton } from "elements";

const MenuDefaultsContainer = styled.div`
  max-width: 1200px;
  margin: 24px auto;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  color: #111827;
  font-size: 14px;
  
  .page-title {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 20px;
    @media (min-width: 768px) {
      flex-direction: row;
      justify-content: space-between;
      align-items: flex-start;
    }
  }
  .page-title h1 {
    margin: 0 0 4px 0;
    font-size: 22px;
    font-weight: 600;
  }
  .page-title p {
    margin: 0;
    color: #6b7280;
    font-size: 13px;
    line-height: 1.55;
  }
  
  .kpi-strip {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
  }
  
  .kpi {
    padding: 10px 14px;
    border: 1px solid #e5e7eb;
    background: #ffffff;
    border-radius: 8px;
    text-align: center;
    min-width: 90px;
    flex: 1;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }
  
  .kpi .n {
    font-size: 20px;
    font-weight: 700;
    color: #2563eb;
    line-height: 1.1;
  }
  
  .kpi .l {
    font-size: 10px;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-top: 3px;
  }
  
  .callout {
    padding: 12px 16px;
    background: #eef2ff;
    border-left: 3px solid #2563eb;
    border-radius: 6px;
    font-size: 13px;
    color: #1e3a8a;
    margin-bottom: 20px;
    line-height: 1.55;
  }
  
  .filter-bar {
    padding: 12px;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    margin-bottom: 12px;
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
  }
  
  .filter-bar input[type=text] {
    padding: 6px 12px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 13px;
    background: white;
    flex: 1;
    min-width: 200px;
    color: #111827;
  }
  
  .filter-bar select {
    padding: 6px 32px 6px 12px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 13px;
    background-color: white;
    color: #374151;
    appearance: none;
    background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23374151' stroke-width='2.5'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 10px center;
    background-size: 12px;
    cursor: pointer;
  }
  
  .filter-bar .count {
    color: #6b7280;
    font-size: 12px;
    margin-left: auto;
    padding: 0 8px;
  }
  
  .toolbar {
    padding: 8px 14px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: white;
    display: flex;
    gap: 6px;
    align-items: center;
    flex-wrap: wrap;
    margin-bottom: 12px;
  }
  
  .toolbar button {
    padding: 6px 12px;
    border: 1px solid #d1d5db;
    background: white;
    border-radius: 6px;
    font-size: 12px;
    cursor: pointer;
    color: #374151;
    font-weight: 500;
    transition: background-color 0.2s;
  }
  
  .toolbar button:hover {
    background: #f9fafb;
  }
  
  .toolbar button.primary {
    background: #2563eb;
    color: white;
    border-color: #2563eb;
  }
  
  .toolbar button.primary:hover {
    background: #1d4ed8;
  }
  
  .toolbar .divider {
    width: 1px;
    height: 22px;
    background: #e5e7eb;
    margin: 0 4px;
  }
  
  .split {
    display: grid;
    grid-template-columns: 1fr;
    gap: 20px;
    align-items: start;
    @media (min-width: 900px) {
      grid-template-columns: 1fr 380px;
    }
  }
  
  .defaults-menu-list {
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    overflow: hidden;
  }
  
  .menu-group {
    border-bottom: 1px solid #e5e7eb;
  }
  
  .menu-group:last-child {
    border-bottom: 0;
  }
  
  .menu-group-header {
    padding: 12px 16px;
    background: #f9fafb;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #6b7280;
    font-weight: 600;
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    user-select: none;
    border-top: 1px solid #e5e7eb;
    border-bottom: 1px solid #e5e7eb;
  }
  
  .menu-group-header:hover {
    background: #f3f4f6;
  }
  
  .menu-group-header .cat-count {
    font-weight: 600;
    color: #9ca3af;
    font-size: 11px;
  }
  
  .menu-row {
    display: grid;
    grid-template-columns: 42px 1fr auto;
    gap: 12px;
    padding: 10px 16px;
    border-top: 1px solid #f3f4f6;
    align-items: center;
    transition: background-color 0.2s;
    background: white;
  }
  
  .menu-row:first-of-type {
    border-top: 0;
  }
  
  .menu-row:hover {
    background: #fafbfc;
  }
  
  .menu-row input[type=checkbox] {
    appearance: none;
    width: 16px;
    height: 16px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    outline: none;
    background-color: white;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    position: relative;
    transition: all 0.2s ease;
    justify-self: center;
  }
  
  .menu-row input[type=checkbox]:checked {
    background-color: #2563eb;
    border-color: #2563eb;
  }
  
  .menu-row input[type=checkbox]:checked::after {
    content: '';
    width: 8px;
    height: 4px;
    border-left: 2px solid white;
    border-bottom: 2px solid white;
    transform: rotate(-45deg);
    position: absolute;
    top: 4px;
  }
  
  .menu-row input[type=checkbox]:disabled {
    background-color: #f3f4f6;
    border-color: #e5e7eb;
    cursor: not-allowed;
  }
  
  .menu-row input[type=checkbox]:disabled:checked::after {
    border-color: #9ca3af;
  }
  
  .menu-row .info .title {
    font-weight: 500;
    font-size: 14px;
    color: #111827;
  }
  
  .menu-row .info .title .icon {
    display: inline-block;
    width: 18px;
    text-align: center;
    margin-right: 6px;
    opacity: 0.8;
  }
  
  .menu-row .info .meta {
    color: #6b7280;
    font-size: 12px;
    margin-top: 2px;
  }
  
  .menu-row .badges {
    display: flex;
    gap: 6px;
  }
  
  .badge {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 10px;
    font-weight: 600;
  }
  
  .badge-required {
    background: #fef3c7;
    color: #92400e;
  }
  
  .badge-role {
    background: #f3e8ff;
    color: #6b21a8;
  }
  
  .badge-enabled {
    background: #dcfce7;
    color: #166534;
  }
  
  .menu-row.child {
    background: white;
  }
  
  .menu-row.child .info {
    margin-left: 24px;
  }
  
  .menu-row.child .info .title {
    font-weight: 400;
    color: #374151;
  }
  
  .menu-row.child .info .title::before {
    content: '└ ';
    color: #9ca3af;
    margin-right: 4px;
  }
  
  .preview {
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 0;
    position: sticky;
    top: 16px;
    align-self: start;
    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  }
  
  .preview-hdr {
    padding: 14px 16px;
    background: #f9fafb;
    border-bottom: 1px solid #e5e7eb;
    border-radius: 8px 8px 0 0;
  }
  
  .preview-hdr h3 {
    margin: 0 0 4px 0;
    font-size: 13px;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-weight: 600;
  }
  
  .preview-hdr p {
    margin: 0;
    font-size: 12px;
    color: #9ca3af;
    line-height: 1.5;
  }
  
  .preview-body {
    padding: 12px 0;
    max-height: 500px;
    overflow-y: auto;
  }
  
  .preview-item {
    padding: 8px 16px;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #374151;
  }
  
  .preview-item .icon {
    width: 16px;
    text-align: center;
    opacity: 0.8;
  }
  
  .preview-item.child {
    padding-left: 40px;
    color: #6b7280;
    font-size: 12px;
  }
  
  .preview-item.child .icon {
    display: none;
  }
  
  .preview-item.role-only {
    color: #6b21a8;
  }
  
  .preview-item.role-only::after {
    content: 'role-gated';
    margin-left: auto;
    font-size: 10px;
    padding: 1px 6px;
    background: #f3e8ff;
    border-radius: 999px;
    font-weight: 600;
  }
  
  .preview-empty {
    padding: 30px 16px;
    text-align: center;
    color: #9ca3af;
    font-size: 12px;
    font-style: italic;
  }
  
  .preview-footer {
    padding: 12px 16px;
    background: #f9fafb;
    border-top: 1px solid #e5e7eb;
    font-size: 11px;
    color: #6b7280;
    border-radius: 0 0 8px 8px;
    line-height: 1.5;
  }
  
  .footer-bar {
    background: #fafbfc;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 14px 26px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 13px;
    margin-top: 24px;
    box-shadow: 0 -1px 2px rgba(0,0,0,0.02);
  }
  
  .footer-bar .status {
    color: #374151;
  }
  
  .footer-bar .actions {
    display: flex;
    gap: 8px;
  }
  
  .footer-bar button {
    padding: 8px 16px;
    border-radius: 6px;
    border: 1px solid #d1d5db;
    background: white;
    color: #374151;
    font-size: 13px;
    cursor: pointer;
    font-weight: 500;
  }
  
  .footer-bar button:hover:not(:disabled) {
    background: #f9fafb;
  }
  
  .footer-bar button.primary {
    background: #2563eb;
    color: white;
    border-color: #2563eb;
  }
  
  .footer-bar button.primary:hover:not(:disabled) {
    background: #1d4ed8;
  }
  
  .footer-bar button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  /* Save confirm modal style */
  .modal-body-container {
    font-size: 13px;
    line-height: 1.6;
    color: #374151;
  }
  
  .modal-body-container ul {
    margin: 6px 0 12px 0;
    padding-left: 22px;
  }
  
  .modal-body-container ul li {
    margin: 4px 0;
  }
  
  .modal-body-container ul li strong {
    color: #111827;
  }
  
  .modal-callout {
    padding: 10px 12px;
    background: #fffbeb;
    border: 1px solid #fde68a;
    border-radius: 6px;
    font-size: 12px;
    color: #78350f;
    margin-top: 12px;
  }
`;

const getMenuIcon = (iconKey, menuKey) => {
  const key = (menuKey || '').toLowerCase();
  if (key.includes('dashboard')) return '🏠';
  if (key.includes('profile')) return '👤';
  if (key.includes('notification')) return '🔔';
  if (key.includes('logout')) return '🚪';
  if (key.includes('inspections.mine') || key.includes('inspections.my')) return '📋';
  if (key.includes('templates.list') || key.includes('template.list')) return '📝';
  if (key.includes('templates.library') || key.includes('template.library')) return '📎';
  if (key.includes('archived')) return '🗂';
  if (key.includes('consolidated')) return '📊';
  if (key.includes('custom')) return '📈';
  if (key.includes('approvals')) return '✅';
  if (key.includes('export')) return '📤';
  if (key.includes('mobile.qr') || key.includes('qr.generate')) return '📱';
  if (key.includes('mobile.download') || key.includes('download')) return '📥';
  if (key.includes('admin.users') || key.includes('user.management')) return '👥';
  if (key.includes('admin.super') || key.includes('super-admin')) return '🔐';
  if (key.includes('menu-defaults') || key.includes('settings')) return '⚙️';

  if (iconKey === 'clipboard-list' || iconKey === 'clipboard') return '📋';
  if (iconKey === 'file-text' || iconKey === 'scroll') return '📝';
  if (iconKey === 'users') return '👥';
  if (iconKey === 'key' || iconKey === 'lock') return '🔐';
  if (iconKey === 'gear' || iconKey === 'cog') return '⚙️';
  return '📄';
};

const categoryLabels = {
  core: 'Core',
  inspections: 'Inspections',
  reports: 'Reports',
  mobile: 'Mobile',
  admin: 'Admin',
};

const getCategoryLabel = (cat) => {
  const key = (cat || '').toLowerCase();
  return categoryLabels[key] || cat || 'Other';
};

const categoryOrder = {
  core: 1,
  inspections: 2,
  reports: 3,
  mobile: 4,
  admin: 5,
};

const getCategoryOrder = (cat) => {
  const key = (cat || '').toLowerCase();
  return categoryOrder[key] || 99;
};

const getGroupLabel = (catItems) => {
  const total = catItems.length;
  const required = catItems.filter(i => i.IsSystemDefault).length;
  const enabled = catItems.filter(i => (i.IsDefaultEnabled || i.IsSystemDefault) && !i.IsRoleGated).length;
  const roleGated = catItems.filter(i => i.IsRoleGated).length;

  if (required === total) {
    return `${total} - ALL REQUIRED`;
  }

  const parts = [`${total} ITEMS`];
  if (enabled > 0) {
    parts.push(`${enabled} ENABLED`);
  }
  if (roleGated > 0) {
    parts.push(`${roleGated} ROLE-GATED`);
  }
  return parts.join(' - ');
};

const MOCK_ITEMS = [
  { MenuId: 1, ParentMenuId: null, MenuKey: 'core.dashboard', Title: 'Dashboard', Route: '/dashboard', IconKey: 'house', Category: 'core', SortOrder: 1, IsActive: true, IsVisible: true, IsDefaultEnabled: true, IsSystemDefault: true, IsRoleGated: false },
  { MenuId: 2, ParentMenuId: null, MenuKey: 'core.profile', Title: 'Profile', Route: '/secure/profile', IconKey: 'user', Category: 'core', SortOrder: 2, IsActive: true, IsVisible: true, IsDefaultEnabled: true, IsSystemDefault: true, IsRoleGated: false },
  { MenuId: 3, ParentMenuId: null, MenuKey: 'core.notifications', Title: 'Notifications', Route: '/secure/notifications', IconKey: 'bell', Category: 'core', SortOrder: 3, IsActive: true, IsVisible: true, IsDefaultEnabled: true, IsSystemDefault: true, IsRoleGated: false },
  { MenuId: 4, ParentMenuId: null, MenuKey: 'core.logout', Title: 'Logout', Route: '/logout', IconKey: 'sign-out', Category: 'core', SortOrder: 4, IsActive: true, IsVisible: true, IsDefaultEnabled: true, IsSystemDefault: true, IsRoleGated: false },
  { MenuId: 5, ParentMenuId: null, MenuKey: 'inspections.mine', Title: 'My Inspections', Route: '/secure/inspections', IconKey: 'clipboard-list', Category: 'inspections', SortOrder: 1, IsActive: true, IsVisible: true, IsDefaultEnabled: true, IsSystemDefault: false, IsRoleGated: false },
  { MenuId: 6, ParentMenuId: 5, MenuKey: 'inspections.start', Title: 'Start new inspection', Route: '/secure/inspections/new', IconKey: 'plus', Category: 'inspections', SortOrder: 2, IsActive: true, IsVisible: true, IsDefaultEnabled: true, IsSystemDefault: false, IsRoleGated: false },
  { MenuId: 7, ParentMenuId: 5, MenuKey: 'inspections.wip', Title: 'In-progress', Route: '/secure/inspections/wip', IconKey: 'clock', Category: 'inspections', SortOrder: 3, IsActive: true, IsVisible: true, IsDefaultEnabled: true, IsSystemDefault: false, IsRoleGated: false },
  { MenuId: 8, ParentMenuId: 5, MenuKey: 'inspections.done', Title: 'Completed', Route: '/secure/inspections/done', IconKey: 'check', Category: 'inspections', SortOrder: 4, IsActive: true, IsVisible: true, IsDefaultEnabled: true, IsSystemDefault: false, IsRoleGated: false },
  { MenuId: 9, ParentMenuId: null, MenuKey: 'templates.list', Title: 'Templates', Route: '/secure/templates', IconKey: 'file-text', Category: 'inspections', SortOrder: 5, IsActive: true, IsVisible: true, IsDefaultEnabled: true, IsSystemDefault: false, IsRoleGated: false },
  { MenuId: 10, ParentMenuId: null, MenuKey: 'templates.library', Title: 'Templates Library', Route: '/secure/templates/library', IconKey: 'paperclip', Category: 'inspections', SortOrder: 6, IsActive: true, IsVisible: true, IsDefaultEnabled: false, IsSystemDefault: false, IsRoleGated: false },
  { MenuId: 11, ParentMenuId: null, MenuKey: 'inspections.archived', Title: 'Archived Inspections', Route: '/secure/inspections/archived', IconKey: 'archive', Category: 'inspections', SortOrder: 7, IsActive: true, IsVisible: true, IsDefaultEnabled: false, IsSystemDefault: false, IsRoleGated: false },
  { MenuId: 12, ParentMenuId: null, MenuKey: 'reports.consolidated', Title: 'Consolidated Report Emails', Route: '/secure/consolidated-reports', IconKey: 'chart-bar', Category: 'reports', SortOrder: 1, IsActive: true, IsVisible: true, IsDefaultEnabled: true, IsSystemDefault: false, IsRoleGated: false },
  { MenuId: 13, ParentMenuId: null, MenuKey: 'reports.custom', Title: 'Custom Reports', Route: '/secure/custom-reports', IconKey: 'chart-line', Category: 'reports', SortOrder: 2, IsActive: true, IsVisible: true, IsDefaultEnabled: true, IsSystemDefault: false, IsRoleGated: false },
  { MenuId: 14, ParentMenuId: null, MenuKey: 'reports.approvals', Title: 'Report Approvals', Route: '/secure/approvals', IconKey: 'check-circle', Category: 'reports', SortOrder: 3, IsActive: true, IsVisible: true, IsDefaultEnabled: false, IsSystemDefault: false, IsRoleGated: true },
  { MenuId: 15, ParentMenuId: null, MenuKey: 'reports.export', Title: 'Export', Route: '/secure/reports/export', IconKey: 'export', Category: 'reports', SortOrder: 4, IsActive: true, IsVisible: true, IsDefaultEnabled: false, IsSystemDefault: false, IsRoleGated: false },
  { MenuId: 16, ParentMenuId: null, MenuKey: 'mobile.qr.generate', Title: 'Generate QR Sticker', Route: '/secure/mobile/qr', IconKey: 'qr-code', Category: 'mobile', SortOrder: 1, IsActive: true, IsVisible: true, IsDefaultEnabled: true, IsSystemDefault: false, IsRoleGated: false },
  { MenuId: 17, ParentMenuId: null, MenuKey: 'mobile.download', Title: 'Download Mobile App', Route: '/secure/mobile/download', IconKey: 'download', Category: 'mobile', SortOrder: 2, IsActive: true, IsVisible: true, IsDefaultEnabled: true, IsSystemDefault: false, IsRoleGated: false },
  { MenuId: 18, ParentMenuId: null, MenuKey: 'admin.users', Title: 'User Management', Route: '/secure/admin/users', IconKey: 'users', Category: 'admin', SortOrder: 1, IsActive: true, IsVisible: true, IsDefaultEnabled: false, IsSystemDefault: false, IsRoleGated: false },
  { MenuId: 19, ParentMenuId: null, MenuKey: 'admin.super', Title: 'Super Admin Console', Route: '/secure/super-admin', IconKey: 'lock', Category: 'admin', SortOrder: 2, IsActive: true, IsVisible: true, IsDefaultEnabled: false, IsSystemDefault: false, IsRoleGated: true },
  { MenuId: 20, ParentMenuId: null, MenuKey: 'admin.menu-defaults', Title: 'Menu Defaults', Route: '/secure/admin/menu-defaults (this page)', IconKey: 'gear', Category: 'admin', SortOrder: 3, IsActive: true, IsVisible: true, IsDefaultEnabled: false, IsSystemDefault: false, IsRoleGated: true },
];

export const MenuDefaultsTab = () => {
  const { successToast, errorToast, warningToast } = useNotification();

  const [items, setItems] = useState([]);
  const [originalState, setOriginalState] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isUsingMock, setIsUsingMock] = useState(false);

  // Filters state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All categories');
  const [selectedStatus, setSelectedStatus] = useState('All statuses');

  // Expansion state
  const [collapsedCategories, setCollapsedCategories] = useState(new Set());

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Load menu defaults
  const fetchMenuDefaults = useCallback(async () => {
    try {
      setLoading(true);
      const res = await MenuSettingService.menuDefaultsList();
      if (res && res.Success && res.Items) {
        setItems(res.Items);
        const orig = {};
        res.Items.forEach(item => {
          orig[item.MenuId] = item.IsDefaultEnabled;
        });
        setOriginalState(orig);
        setIsUsingMock(false);
      } else {
        throw new Error('Unsuccessful API response');
      }
    } catch (err) {
      console.warn("Failed to load MenuDefaults from API, using mock fallback:", err);
      // Mock Fallback
      setItems(MOCK_ITEMS);
      const orig = {};
      MOCK_ITEMS.forEach(item => {
        orig[item.MenuId] = item.IsDefaultEnabled;
      });
      setOriginalState(orig);
      setIsUsingMock(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMenuDefaults();
  }, [fetchMenuDefaults]);

  // Extract unique categories for filter dropdown
  const categories = useMemo(() => {
    const cats = Array.from(new Set(items.map(item => item.Category).filter(Boolean)));
    cats.sort((a, b) => getCategoryOrder(a) - getCategoryOrder(b));
    return cats;
  }, [items]);

  // Build structured, sorted tree within each category group
  const groupedSortedItems = useMemo(() => {
    const groups = {};
    items.forEach(item => {
      const cat = item.Category || 'Other';
      if (!groups[cat]) {
        groups[cat] = [];
      }
      groups[cat].push(item);
    });

    const structured = {};
    Object.keys(groups).forEach(cat => {
      const groupItems = groups[cat];
      const parents = groupItems.filter(i => !i.ParentMenuId);
      parents.sort((a, b) => a.SortOrder - b.SortOrder);

      const sortedList = [];
      parents.forEach(p => {
        sortedList.push(p);
        const children = groupItems.filter(i => i.ParentMenuId === p.MenuId);
        children.sort((a, b) => a.SortOrder - b.SortOrder);
        sortedList.push(...children);
      });

      // Catch orphans
      const orphans = groupItems.filter(i => i.ParentMenuId && !parents.some(p => p.MenuId === i.ParentMenuId));
      orphans.sort((a, b) => a.SortOrder - b.SortOrder);
      sortedList.push(...orphans);

      structured[cat] = sortedList;
    });

    return structured;
  }, [items]);

  // Apply filters
  const filteredGroupedItems = useMemo(() => {
    const filtered = {};
    Object.keys(groupedSortedItems).forEach(cat => {
      const list = groupedSortedItems[cat].filter(item => {
        // Search filter (title, key, route)
        const matchSearch = !searchQuery ||
          item.Title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.MenuKey.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (item.Route && item.Route.toLowerCase().includes(searchQuery.toLowerCase()));

        // Category filter
        const matchCategory = selectedCategory === 'All categories' ||
          (item.Category && item.Category.toLowerCase() === selectedCategory.toLowerCase());

        // Status filter
        let matchStatus = true;
        if (selectedStatus === 'Enabled') {
          matchStatus = item.IsSystemDefault || item.IsDefaultEnabled;
        } else if (selectedStatus === 'Disabled') {
          matchStatus = !item.IsSystemDefault && !item.IsDefaultEnabled && !item.IsRoleGated;
        } else if (selectedStatus === 'Required') {
          matchStatus = item.IsSystemDefault;
        } else if (selectedStatus === 'Role-gated') {
          matchStatus = item.IsRoleGated;
        }

        return matchSearch && matchCategory && matchStatus;
      });

      if (list.length > 0) {
        filtered[cat] = list;
      }
    });
    return filtered;
  }, [groupedSortedItems, searchQuery, selectedCategory, selectedStatus]);

  // Compute stats in real time based on current items state
  const stats = useMemo(() => {
    const total = items.length;
    const enabled = items.filter(item => item.IsSystemDefault || item.IsDefaultEnabled).length;
    const required = items.filter(item => item.IsSystemDefault).length;
    const roleGated = items.filter(item => item.IsRoleGated).length;

    return { total, enabled, required, roleGated };
  }, [items]);

  // Count showing items
  const showingCount = useMemo(() => {
    let count = 0;
    Object.keys(filteredGroupedItems).forEach(cat => {
      count += filteredGroupedItems[cat].length;
    });
    return count;
  }, [filteredGroupedItems]);

  // Checkbox toggle logic
  const handleToggle = (menuId, checked) => {
    setItems(prev => prev.map(item => {
      if (item.MenuId === menuId && !item.IsSystemDefault && !item.IsRoleGated) {
        return { ...item, IsDefaultEnabled: checked };
      }
      return item;
    }));
  };

  // Group collapsed toggling
  const toggleGroupCollapse = (cat) => {
    setCollapsedCategories(prev => {
      const next = new Set(prev);
      if (next.has(cat)) {
        next.delete(cat);
      } else {
        next.add(cat);
      }
      return next;
    });
  };

  // Toolbar Actions
  const handleCheckAllVisible = () => {
    const visibleIds = [];
    Object.keys(filteredGroupedItems).forEach(cat => {
      filteredGroupedItems[cat].forEach(item => {
        if (!item.IsSystemDefault && !item.IsRoleGated) {
          visibleIds.push(item.MenuId);
        }
      });
    });

    setItems(prev => prev.map(item => {
      if (visibleIds.includes(item.MenuId)) {
        return { ...item, IsDefaultEnabled: true };
      }
      return item;
    }));
  };

  const handleUncheckAllVisible = () => {
    const visibleIds = [];
    Object.keys(filteredGroupedItems).forEach(cat => {
      filteredGroupedItems[cat].forEach(item => {
        if (!item.IsSystemDefault && !item.IsRoleGated) {
          visibleIds.push(item.MenuId);
        }
      });
    });

    setItems(prev => prev.map(item => {
      if (visibleIds.includes(item.MenuId)) {
        return { ...item, IsDefaultEnabled: false };
      }
      return item;
    }));
  };

  const handleExpandAll = () => {
    setCollapsedCategories(new Set());
  };

  const handleCollapseAll = () => {
    const allCats = new Set(Object.keys(groupedSortedItems));
    setCollapsedCategories(allCats);
  };

  // Diff Calculations
  const diff = useMemo(() => {
    const enabled = [];
    const disabled = [];
    items.forEach(item => {
      if (item.IsSystemDefault || item.IsRoleGated) return;
      const wasEnabled = !!originalState[item.MenuId];
      const isEnabled = !!item.IsDefaultEnabled;
      if (isEnabled !== wasEnabled) {
        if (isEnabled) {
          enabled.push(item);
        } else {
          disabled.push(item);
        }
      }
    });
    return { enabled, disabled, count: enabled.length + disabled.length };
  }, [items, originalState]);

  // Cancel/Reset Changes
  const handleCancel = () => {
    setItems(prev => prev.map(item => ({
      ...item,
      IsDefaultEnabled: !!originalState[item.MenuId],
    })));
  };

  // Submit Save Changes
  const handleSaveSubmit = async () => {
    try {
      setSaving(true);

      if (isUsingMock) {
        // Simulate API call and success
        await new Promise(resolve => setTimeout(resolve, 800));
        successToast(`[MOCK MODE] Saved successfully. ${diff.enabled.length} enabled, ${diff.disabled.length} disabled.`);

        // Update mock items to reflect the saved state
        items.forEach(item => {
          const match = MOCK_ITEMS.find(m => m.MenuId === item.MenuId);
          if (match) {
            match.IsDefaultEnabled = item.IsDefaultEnabled;
          }
        });

        const orig = {};
        items.forEach(item => {
          orig[item.MenuId] = item.IsDefaultEnabled;
        });
        setOriginalState(orig);
        setIsModalOpen(false);
        return;
      }

      const req = {
        Enabled: diff.enabled.map(item => item.MenuId),
        Disabled: diff.disabled.map(item => item.MenuId),
      };

      const res = await MenuSettingService.menuDefaultsSave(req);
      if (res && res.Success) {
        const rejected = [...(res.RejectedSystemDefaults || []), ...(res.RejectedRoleGated || [])];
        if (rejected.length > 0) {
          warningToast(`${rejected.length} items couldn't be updated (system defaults or role-gated).`);
        } else {
          successToast(`Saved successfully. ${res.EnabledApplied || 0} enabled, ${res.DisabledApplied || 0} disabled.`);
        }
        setIsModalOpen(false);
        // Reload to sync with DB state
        await fetchMenuDefaults();
      } else {
        errorToast('Failed to save menu defaults.');
      }
    } catch (err) {
      console.error(err);
      errorToast('Error saving changes.');
    } finally {
      setSaving(false);
    }
  };

  // Compute live preview list
  const previewList = useMemo(() => {
    // Collect all sorted items based on category order
    const orderedCats = Object.keys(groupedSortedItems).sort((a, b) => getCategoryOrder(a) - getCategoryOrder(b));
    const list = [];

    orderedCats.forEach(cat => {
      groupedSortedItems[cat].forEach(item => {
        // Show if checked (enabled or required) OR if role-gated
        const isVisible = item.IsSystemDefault || item.IsDefaultEnabled || item.IsRoleGated;
        if (isVisible) {
          list.push(item);
        }
      });
    });

    return list;
  }, [groupedSortedItems]);

  const previewCounts = useMemo(() => {
    const roleGated = previewList.filter(item => item.IsRoleGated).length;
    const defaults = previewList.length - roleGated;
    return { defaults, roleGated };
  }, [previewList]);

  // Sort categories for layout rendering
  const sortedCategories = useMemo(() => {
    return Object.keys(filteredGroupedItems).sort((a, b) => getCategoryOrder(a) - getCategoryOrder(b));
  }, [filteredGroupedItems]);

  if (loading) {
    return (
      <div style={{ padding: '40px' }}>
        <LoaderBar />
      </div>
    );
  }

  return (
    <MenuDefaultsContainer>
      {/* Title block */}
      <div className="page-title">
        <div>
          <h1>Menu Defaults</h1>
          <p>Check every menu that should appear for all users by default. Save applies immediately — new logins pick up changes on their next session, active sessions on their next dashboard load.</p>
        </div>
        <div className="kpi-strip">
          <div className="kpi">
            <div className="n">{stats.total}</div>
            <div className="l">Total menus</div>
          </div>
          <div className="kpi">
            <div className="n">{stats.enabled}</div>
            <div className="l">Enabled default</div>
          </div>
          <div className="kpi">
            <div className="n">{stats.required}</div>
            <div className="l">Required</div>
          </div>
          <div className="kpi">
            <div className="n">{stats.roleGated}</div>
            <div className="l">Role-gated</div>
          </div>
        </div>
      </div>

      {/* Legend Block */}
      <div className="callout">
        <strong>Legend:</strong>
        <span className="badge badge-required" style={{ marginLeft: '6px' }}>Required</span> — system default, always on, can't be unchecked.
        <span className="badge badge-role" style={{ marginLeft: '6px' }}>Role-gated</span> — permission-managed elsewhere, not toggled here.
        <span className="badge badge-enabled" style={{ marginLeft: '6px' }}>Enabled</span> — currently in the default set. Uncheck to hide from everyone.
      </div>

      {/* Filters block */}
      <div className="filter-bar">
        <input
          type="text"
          placeholder="Search menus by title, key, or route…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}>
          <option value="All categories">All categories</option>
          {categories.map(cat => (
            <option key={cat} value={cat}>{getCategoryLabel(cat)}</option>
          ))}
        </select>
        <select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
          <option value="All statuses">All statuses</option>
          <option value="Enabled">Enabled</option>
          <option value="Disabled">Disabled</option>
          <option value="Required">Required</option>
          <option value="Role-gated">Role-gated</option>
        </select>
        <span className="count">Showing {showingCount} of {stats.total}</span>
      </div>

      {/* Toolbar block */}
      <div className="toolbar">
        <button type="button" className="primary" onClick={handleCheckAllVisible}>Check all visible</button>
        <button type="button" onClick={handleUncheckAllVisible}>Uncheck all visible</button>
        <div className="divider" />
        <button type="button" onClick={handleExpandAll}>Expand all</button>
        <button type="button" onClick={handleCollapseAll}>Collapse all</button>
      </div>

      {/* Main Split Layout */}
      <div className="split">
        {/* Menu list panel */}
        <div className="defaults-menu-list">
          {sortedCategories.length === 0 ? (
            <div style={{ padding: '30px', textAlign: 'center', color: '#9ca3af', fontStyle: 'italic' }}>
              No menus match the current filters.
            </div>
          ) : (
            sortedCategories.map(cat => {
              const catItems = filteredGroupedItems[cat];
              const isCollapsed = collapsedCategories.has(cat);

              return (
                <div key={cat} className="menu-group">
                  <div className="menu-group-header" onClick={() => toggleGroupCollapse(cat)}>
                    <span>{getCategoryLabel(cat)}</span>
                    <span className="cat-count">
                      {getGroupLabel(catItems)} {isCollapsed ? '▼' : '▲'}
                    </span>
                  </div>
                  {!isCollapsed && catItems.map(item => {
                    const isChecked = item.IsSystemDefault || item.IsDefaultEnabled;
                    const isDisabled = item.IsSystemDefault || item.IsRoleGated;

                    let badgeType = '';
                    let badgeLabel = '';
                    if (item.IsSystemDefault) {
                      badgeType = 'required';
                      badgeLabel = 'Required';
                    } else if (item.IsRoleGated) {
                      badgeType = 'role';
                      badgeLabel = 'Role-gated';
                    } else if (item.IsDefaultEnabled) {
                      badgeType = 'enabled';
                      badgeLabel = 'Enabled';
                    }

                    return (
                      <div
                        key={item.MenuId}
                        className={`menu-row ${item.ParentMenuId ? 'child' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          disabled={isDisabled}
                          onChange={(e) => handleToggle(item.MenuId, e.target.checked)}
                        />
                        <div className="info">
                          <div className="title">
                            {!item.ParentMenuId && (
                              <span className="icon">
                                {getMenuIcon(item.IconKey, item.MenuKey)}
                              </span>
                            )}
                            {item.Title}
                          </div>
                          <div className="meta">
                            {item.MenuKey} - Route: {item.Route || '(none)'}
                          </div>
                        </div>
                        <div className="badges">
                          {badgeType && (
                            <span className={`badge badge-${badgeType}`}>
                              {badgeLabel}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>

        {/* Live Preview panel */}
        <div className="preview">
          <div className="preview-hdr">
            <h3>Preview — what a new user sees</h3>
            <p>Live render of the sidebar based on your current checkbox state. Refreshes as you check/uncheck.</p>
          </div>
          <div className="preview-body">
            {previewList.length === 0 ? (
              <div className="preview-empty">No default menus enabled</div>
            ) : (
              previewList.map(item => (
                <div
                  key={item.MenuId}
                  className={`preview-item ${item.ParentMenuId ? 'child' : ''} ${item.IsRoleGated ? 'role-only' : ''}`}
                >
                  <span className="icon">
                    {getMenuIcon(item.IconKey, item.MenuKey)}
                  </span>
                  {item.Title}
                </div>
              ))
            )}
          </div>
          <div className="preview-footer">
            <strong>{previewCounts.defaults} default</strong> + <strong>{previewCounts.roleGated} role-gated</strong> (shown when granted). Regular users get default only. Managers add "Report Approvals". Super Admins add "Super Admin Console" + "Menu Defaults".
          </div>
        </div>
      </div>

      {/* Bottom status & actions footer */}
      <div className="footer-bar">
        <div className="status">
          {diff.count > 0 ? (
            <strong>{diff.count} unsaved {diff.count === 1 ? 'change' : 'changes'}. Save to apply for all users.</strong>
          ) : (
            <span>No unsaved changes.</span>
          )}
        </div>
        <div className="actions">
          <button type="button" onClick={handleCancel} disabled={diff.count === 0}>Cancel</button>
          <button
            type="button"
            className="primary"
            onClick={() => setIsModalOpen(true)}
            disabled={diff.count === 0}
          >
            Save changes
          </button>
        </div>
      </div>

      {/* Save Confirmation Modal */}
      <Modal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        size="small"
      >
        <Modal.Header>
          Apply these menu-default changes?
        </Modal.Header>
        <Modal.Content>
          <div className="modal-body-container">
            <p>These changes take effect <strong>immediately</strong> for every user in the system. Active sessions will see the new sidebar on their next dashboard load; new logins pick it up right away.</p>

            {diff.enabled.length > 0 && (
              <>
                <p><strong>Adding as default ({diff.enabled.length}):</strong></p>
                <ul>
                  {diff.enabled.map(item => (
                    <li key={item.MenuId}>
                      {item.Title} <code>{item.MenuKey}</code>
                    </li>
                  ))}
                </ul>
              </>
            )}

            {diff.disabled.length > 0 && (
              <>
                <p><strong>Removing from default ({diff.disabled.length}):</strong></p>
                <ul>
                  {diff.disabled.map(item => (
                    <li key={item.MenuId}>
                      {item.Title} <code>{item.MenuKey}</code>
                    </li>
                  ))}
                </ul>
              </>
            )}

            <div className="modal-callout">
              Removing a menu from default hides it from every user except those explicitly granted via role or user permission (existing mechanism, unchanged). It does not delete the menu itself.
            </div>

            <FlexRow className="justify-content-end" style={{ marginTop: '20px', gap: '8px' }}>
              <AppPrimaryButton onClick={handleSaveSubmit} disabled={saving}>
                {saving ? 'Saving...' : `Save ${diff.count} changes`}
              </AppPrimaryButton>
              <AppButton onClick={() => setIsModalOpen(false)} disabled={saving}>
                Cancel
              </AppButton>
            </FlexRow>
          </div>
        </Modal.Content>
      </Modal>
    </MenuDefaultsContainer>
  );
};
